import React, { useState } from 'react';
import { useSelector } from "react-redux";
import moment from "moment/moment";
import { endOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import Accordion from 'react-bootstrap/Accordion';
import { Dropdown, Card, Badge } from 'react-bootstrap';
import { isMobileOnly } from "react-device-detect";
import { toastr } from "react-redux-toastr";
import { FaFilePdf } from "react-icons/fa6";
import { FaFileExcel } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { HiArrowLongRight } from "react-icons/hi2";
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';

import { CustomButtonToggle, CustomMenu } from '../../DropdownComponents';
import DateDropdown from "../../dump/menuComponents/DateDropdown";
import { getDateRangeFromTabKey, getInitTabKey } from "./ReportsHelpers";
import CustomButton from "../../CustomButton";
import AddSubscriptionModal from "./Modals/AddSubscriptionModal";
import SelectVehicleModal from "./Modals/SelectVehicleModal";
import BaseTooltip from "../BaseTooltip";
import { exportHistoryReport, generateHistoryReport } from "../../../action/ReportsActions";
import { tableNoPaginationOptions } from '../../helpers/table';
import '../../../component/common/MobileGridCards.sass';

const formatDuration = (seconds) => {
    if (seconds == null) return '-';

    const d = moment.duration(seconds, 'seconds');
    const hours = Math.floor(d.asHours());
    const minutes = d.minutes();
    const secs = d.seconds();

    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}min`);
    if (secs || parts.length === 0) parts.push(`${secs}sec`);

    return parts.join(' ');
}

const formatDistance = (unit) => (cell) => `${cell} ${unit === 'miles' ? 'Mi' : 'Km'}`

const formatTime = (time) => time ? moment(time, 'HH:mm').format('h:mm A') : '-'

const tripColumns = ['segment', 'driver', 'departure', 'start_location', 'arrival', 'end_location', 'drive_time', 'distance', 'stop_duration'];

const RouteHistoryReport = ({ selectedReport, goBack }) => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'reportsPage.standardReportTable' });
    const company = useSelector(state => state.company.company);
    const initTabKey = getInitTabKey(selectedReport.defaultDateRange);
    const dateRange = getDateRangeFromTabKey(initTabKey);
    const [startDate, setStartDate] = useState(dateRange[0]);
    const [endDate, setEndDate] = useState(dateRange[1]);
    const [showAddSubscription, setShowAddSubscription] = useState(false);
    const [showSelectVehicles, setShowSelectVehicles] = useState(false);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [reportData, setReportData] = useState([]);
    const reportGenerationDisabled = !selectedVehicles.length;
    const isMobile = isMobileOnly || window.innerWidth <= 991

    const changeDate = (selectedStartDate, selectedEndDate) => {
        setStartDate(selectedStartDate ? moment(selectedStartDate).format('YYYYMMDD') : startDate);
        setEndDate(selectedEndDate ? moment(selectedEndDate).format('YYYYMMDD') : endDate);
    };

    const goBackFunc = () => {
        goBack(); // ToDo: Add directly
    };

    const clearSelectedData = () => {
        setSelectedVehicles([]);
    };

    const generateReport = () => {
        generateHistoryReport({
            device_ids: selectedVehicles.join(','),
            start_date: startDate,
            end_date: endDate,
            company_id: company.company_id,
            page_number: 1,
            page_size: 20,
        }).then(res => {
            const { report } = res.data.response;
            setReportData(report);
        }).catch(err => {
            console.error("Error generating report", err);
            toastr.error(t('errGeneratingReport'))
        })
    };

    const exportToExcel = () => {
        exportHistoryReport({
            device_ids: selectedVehicles.join(','),
            start_date: startDate,
            end_date: endDate,
            company_id: company.company_id,
        }).then(res => {
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `route_history_${company.name}_${startDate}_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch(err => {
            console.error('Error exporting report', err);
            toastr.error(t('errGeneratingReport'));
        });
    };

    const exportToPdf = async () => {
        if (!reportData.length) return;
        try {
            const html2canvas = (await import('html2canvas')).default;
            const { jsPDF } = await import('jspdf');

            const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const padding = 24;
            let cursorY = padding;

            const captureElement = async (el) => {
                const canvas = await html2canvas(el, {
                    scale: 2,
                    useCORS: true,
                    scrollY: 0,
                    width: el.offsetWidth,
                    height: el.scrollHeight,
                    windowHeight: el.scrollHeight,
                });
                const imgData = canvas.toDataURL('image/png');
                const imgHeight = (canvas.height * (pdfWidth - padding * 2)) / canvas.width;
                return { imgData, imgHeight };
            };

            const addBlock = async (el) => {
                const { imgData, imgHeight } = await captureElement(el);
                if (cursorY + imgHeight > pageHeight - padding) {
                    pdf.addPage();
                    cursorY = padding;
                }
                pdf.addImage(imgData, 'PNG', padding, cursorY, pdfWidth - padding * 2, imgHeight);
                cursorY += imgHeight + 8;
            };

            const wrapper = document.createElement('div');
            wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;width:1100px;background:#fff;font-family:inherit;font-size:12px;';
            document.body.appendChild(wrapper);

            for (const item of reportData) {
                const header = document.createElement('div');
                header.style.cssText = 'padding:8px 0;';
                header.innerHTML = `
                    <h4 style="margin:0 0 8px;font-size:15px;font-weight:600;">${item.asset_name}</h4>
                    <div style="display:flex;gap:24px;flex-wrap:wrap;font-size:12px;">
                        <span><b>${t('totalDistance')}:</b> ${item.summary.total_distance} ${item.summary.unit === 'miles' ? 'Mi' : 'Km'}</span>
                        <span><b>${t('drive_time')}:</b> ${formatDuration(item.summary.total_drive_time)}</span>
                        <span><b>${t('totalStops')}:</b> ${item.summary.total_stops}</span>
                        <span><b>${t('earliestStart')}:</b> ${formatTime(item.summary.earliest_start)}</span>
                        <span><b>${t('latestEnd')}:</b> ${formatTime(item.summary.latest_end)}</span>
                    </div>
                `;
                wrapper.appendChild(header);
                await addBlock(header);
                wrapper.removeChild(header);

                const grouped = item.trips.reduce((acc, trip) => {
                    if (!acc[trip.date]) acc[trip.date] = [];
                    acc[trip.date].push(trip);
                    return acc;
                }, {});

                for (const [date, trips] of Object.entries(grouped)) {
                    const dayTotal = item.daily_totals.find(dt => dt.date === date);
                    const block = document.createElement('div');
                    block.style.cssText = 'padding:4px 0;';
                    block.innerHTML = `
                        <h6 style="font-size:12px;font-weight:600;margin:0 0 4px;">${moment(date).format('MMMM D, YYYY')}</h6>
                        <table style="width:100%;border-collapse:collapse;font-size:11px;">
                            <thead>
                                <tr style="background:#0071b6;">
                                    ${tripColumns.map(k => `<th style="padding:5px 7px;border:1px solid #ddd;text-align:left;white-space:nowrap;color:#fff;">${t(k)}</th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                ${trips.map((trip, i) => `
                                    <tr style="background:${i % 2 === 0 ? '#fff' : '#f2f2f2'};">
                                        <td style="padding:5px 7px;border:1px solid #ddd;">${i + 1}</td>
                                        ${tripColumns.filter(k => k !== 'segment').map(k => {
                                            let val;
                                            if (['drive_time','stop_duration'].includes(k)) val = formatDuration(trip[k]);
                                            else if (k === 'distance') val = formatDistance(item.summary.unit)(trip[k]);
                                            else if (['departure','arrival'].includes(k)) val = formatTime(trip[k]);
                                            else val = trip[k] || '-';
                                            return `<td style="padding:5px 7px;border:1px solid #ddd;">${val}</td>`;
                                        }).join('')}
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                        ${dayTotal ? `
                            <div style="display:flex;gap:16px;padding:4px 0;font-size:11px;color:#555;border-top:1px solid #eee;">
                                <span><b>${t('distance')}:</b> ${formatDistance(item.summary.unit)(dayTotal.total_distance)}</span>
                                <span><b>${t('drive_time')}:</b> ${formatDuration(dayTotal.drive_time)}</span>
                                <span><b>${t('stops')}:</b> ${dayTotal.total_stops}</span>
                                <span><b>${t('stop_duration')}:</b> ${formatDuration(dayTotal.stop_time)}</span>
                            </div>
                        ` : ''}
                    `;
                    wrapper.appendChild(block);
                    await addBlock(block);
                    wrapper.removeChild(block);
                }

                cursorY += 16;
            }

            document.body.removeChild(wrapper);
            pdf.save(`route_history_${company.name}_${startDate}_${endDate}.pdf`);
        } catch (err) {
            console.error('Error exporting PDF', err);
            toastr.error(t('errGeneratingReport'));
        }
    };

    return (
        <div className="report">
            <div className="page-subheader">
                <div className="subheader-section search-flex report-search ifta-section">
                <DateDropdown
                    start_date={startDate}
                    end_date={endDate}
                    onSelectDate={changeDate}
                    maxDate={endOfDay(new Date())}
                />
                </div>
                <div className="subheader-section">
                    <CustomButton variant="primary-outline" size="sm" onClick={() => setShowAddSubscription(true)}>
                        {t('btnAutomateReport')}
                    </CustomButton>
                    <CustomButton variant="secondary-outline" onClick={goBackFunc}>{t('btnBack')}</CustomButton>
               </div>
            </div>
            <main className="main-content-block">
                <div className="table-header">
                    <span className="report-title">{t('routeHistoryReport')}</span>
                </div>
                <div className="history-report-body">
                    <div className="report-body-header">
                        <CustomButton
                            variant="primary-outline"
                            size="sm"
                            onClick={() => setShowSelectVehicles(true)}
                        >
                            {selectedVehicles.length ? t('selected', { selected: selectedVehicles.length }) : t('selectVehicles')}
                        </CustomButton>
                        <BaseTooltip
                            body={t('historyReportTooltip')}
                            {...(!reportGenerationDisabled && { show: false })}
                        >
                            <div>
                                <CustomButton
                                    variant="primary"
                                    size="sm"
                                    disabled={reportGenerationDisabled}
                                    onClick={generateReport}
                                >
                                    {t('generateReport')}
                                </CustomButton>
                            </div>
                        </BaseTooltip>
                        <CustomButton
                            variant="primary-outline"
                            size="sm"
                            onClick={clearSelectedData}
                        >
                            {t('clear')}
                        </CustomButton>
                    </div>
                    {!!reportData.length && <div className="report-body-data">
                        <div className="report-body-data-header">
                            <span className="report-body-data-title">{t('summary')}</span>
                            <div className="report-body-data-actions">
                                <Dropdown className="actions-dropdown" role="menuitem">
                                    <Dropdown.Toggle as={CustomButtonToggle} variant="primary" prefix="actions dropdown-toggle btn btn-default">
                                        {t('export')}
                                        <span className="caret" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu as={CustomMenu}>
                                        <Dropdown.Item onClick={exportToExcel}><FaFileExcel />Excel</Dropdown.Item>
                                        <Dropdown.Item onClick={exportToPdf}><FaFilePdf />PDF</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                        <Accordion flush>
                            {reportData.map((item, index) => (
                                <Accordion.Item key={`report_item_${index}`} eventKey={`report_item_${index}`}>
                                    <Accordion.Header>{item.asset_name}</Accordion.Header>
                                    <Accordion.Body>
                                        <div className={`asset-summary${isMobile ? ' mobile' : ''}`}>
                                            <div className="asset-summary-item">
                                                <span>{t('totalDistance')}</span>
                                                <strong>{formatDistance(item.summary.unit)(item.summary.total_distance)}</strong>
                                            </div>
                                            <div className="asset-summary-item">
                                                <span>{t('drive_time')}</span>
                                                <strong>{formatDuration(item.summary.total_drive_time)}</strong>
                                            </div>
                                            <div className="asset-summary-item">
                                                <span>{t('totalStops')}</span>
                                                <strong>{item.summary.total_stops}</strong>
                                            </div>
                                            <div className="asset-summary-item">
                                                <span>{t('earliestStart')}</span>
                                                <strong>{formatTime(item.summary.earliest_start)}</strong>
                                            </div>
                                            <div className="asset-summary-item">
                                                <span>{t('latestEnd')}</span>
                                                <strong>{formatTime(item.summary.latest_end)}</strong>
                                            </div>
                                        </div>
                                        <div className="asset-trips">
                                            {(() => {
                                                const grouped = item.trips.reduce((acc, trip) => {
                                                    if (!acc[trip.date]) acc[trip.date] = []
                                                    acc[trip.date].push(trip)
                                                    return acc
                                                }, {})
                                                return Object.entries(grouped).map(([date, trips]) => {
                                                    const dayTotal = item.daily_totals.find(dt => dt.date === date)
                                                    return (
                                                        <div key={date} className="date-group">
                                                            <h6 className="date-group-title">{moment(date).format('MMMM D, YYYY')}</h6>
                                                            {isMobile ? (
                                                                <div className="mobile-grid-cards-container">
                                                                    {trips.map((trip, i) => (
                                                                        <Card key={i} className="mobile-grid-card">
                                                                            <Card.Body>
                                                                                <div className="trip-card-header">
                                                                                    <span className="trip-card-segment">{t('segment')} {i + 1}</span>
                                                                                    {trip.driver && <Badge className="driver-badge">{trip.driver}</Badge>}
                                                                                </div>
                                                                                {(trip.departure || trip.arrival || trip.drive_time != null || trip.distance != null || trip.stop_duration != null) && (
                                                                                    <div className="trip-card-row">
                                                                                        {(trip.departure || trip.arrival) && <span>{formatTime(trip.departure)} <HiArrowLongRight /> {formatTime(trip.arrival)}</span>}
                                                                                        <span>
                                                                                            {trip.drive_time != null && <>{formatDuration(trip.drive_time)}</>}
                                                                                            {trip.drive_time != null && trip.distance != null && ' | '}
                                                                                            {trip.distance != null && <>{formatDistance(item.summary.unit)(trip.distance)}</>}
                                                                                            {' | '}{t('stop')}: {formatDuration(trip.stop_duration)}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {(trip.start_location || trip.end_location) && (
                                                                                    <div className="trip-card-locations">
                                                                                        <MdLocationOn className="location-icon" color="#0071b6" />
                                                                                        <span>{`${trip.start_location || '-'}`}</span>
                                                                                        <HiArrowLongRight />
                                                                                        <span>{`${trip.end_location || '-'}`}</span>
                                                                                    </div>
                                                                                )}
                                                                            </Card.Body>
                                                                        </Card>
                                                                    ))}
                                                                    {dayTotal && (
                                                                        <Card className="mobile-grid-card trip-card-totals">
                                                                            <Card.Body>
                                                                                <div className="trip-card-totals-title">{t('dailyTotals')}</div>
                                                                                <div className="trip-card-totals-row">
                                                                                    <span>{t('distance')}: <strong>{formatDistance(item.summary.unit)(dayTotal.total_distance)}</strong></span>
                                                                                    <span className="trip-card-totals-sep">|</span>
                                                                                    <span>{t('drive_time')}: <strong>{formatDuration(dayTotal.drive_time)}</strong></span>
                                                                                </div>
                                                                                <div className="trip-card-totals-row">
                                                                                    <span>{t('stops')}: <strong>{dayTotal.total_stops}</strong></span>
                                                                                    <span className="trip-card-totals-sep">|</span>
                                                                                    <span>{t('stop_duration')}: <strong>{formatDuration(dayTotal.stop_time)}</strong></span>
                                                                                </div>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <BootstrapTable
                                                                       data={trips}
                                                                       {...tableNoPaginationOptions}
                                                                    >
                                                                        <TableHeaderColumn dataField="route_id" isKey hidden />
                                                                        {tripColumns.map(key => {
                                                                            const formatter = key === 'segment'
                                                                                ? (cell, row, extra, index) => index + 1
                                                                                : ['drive_time', 'stop_duration'].includes(key)
                                                                                ? formatDuration
                                                                                : key === 'distance'
                                                                                    ? formatDistance(item.summary.unit)
                                                                                    : ['departure', 'arrival'].includes(key)
                                                                                        ? formatTime
                                                                                        : (cell) => cell || '-'
                                                                            return (
                                                                                <TableHeaderColumn key={key} dataField={key} dataFormat={formatter}>
                                                                                    {t(key)}
                                                                                </TableHeaderColumn>
                                                                            )
                                                                        })}
                                                                    </BootstrapTable>
                                                                    {dayTotal && (
                                                                        <div className="daily-totals-row">
                                                                            <span className="daily-totals-label">{t('dailyTotals')}</span>
                                                                            <div className="daily-totals-stats">
                                                                                <span>{t('distance')}: <strong>{formatDistance(item.summary.unit)(dayTotal.total_distance)}</strong></span>
                                                                                <span>{t('drive_time')}: <strong>{formatDuration(dayTotal.drive_time)}</strong></span>
                                                                                <span>{t('stops')}: <strong>{dayTotal.total_stops}</strong></span>
                                                                                <span>{t('stop_duration')}: <strong>{formatDuration(dayTotal.stop_time)}</strong></span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    )
                                                })
                                            })()}
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </div>}
                </div>
            </main>
            {showAddSubscription && (
                <AddSubscriptionModal
                    onHide={() => setShowAddSubscription(false)}
                    report={{
                        type: 'standard',
                        report_id: selectedReport.id,
                    }}
                />
            )}
            {showSelectVehicles ? (
                <SelectVehicleModal
                    show={showSelectVehicles}
                    selectedVehicles={selectedVehicles}
                    setSelectedVehicles={setSelectedVehicles}
                    onHide={() => setShowSelectVehicles(false)}
                />
            ) : ''}
        </div>
    )
};

export default RouteHistoryReport;
